import { ThreeDStockContentImageLensProps } from './ThreeDStockContentImageLensTypes';

import { useThreeDStockContentImageLens } from './hooks/useThreeDStockContentImageLens';

import { ThreeDStockContentImageLensResizeButton } from './components/ThreeDStockContentImageLensResizeButton';
import { ThreeDStockContentImageLensSelectionOverlay } from './components/ThreeDStockContentImageLensSelectionOverlay';
import { ThreeDStockContentImageLensLoadingOverlay } from './components/ThreeDStockContentImageLensLoadingOverlay';

import { PureIconButtonHelper } from '../../../../../helpers/buttons/PureIconButtonHelper';

import { IconsEnum } from '../../../../../assets/icons/types';

export type {
  ImagePoint,
  ImageSelection,
  ImageSelectionPayload,
  InitialSelectionUnit,
  InitialSelection,
  SingleClickSelectionResolution,
  ThreeDStockContentImageLensProps
} from './ThreeDStockContentImageLensTypes';

function ThreeDStockContentImageLens({
  imageUrl,
  onSelectedImage,
  onClearSearch,
  minSelectionSize,
  onResolveSingleClickSelection,
  initialSelection,
  isLoading = false,
  loadingText
}: ThreeDStockContentImageLensProps) {
  const {
    containerRef,
    imgRef,
    canvasRef,
    selection,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onHandlePointerDown,
    onImgLoad
  } = useThreeDStockContentImageLens({
    imageUrl,
    onSelectedImage,
    minSelectionSize,
    onResolveSingleClickSelection,
    initialSelection
  });

  return (
    <div className="relative overflow-hidden w-full h-[50vh] rounded-lg flex items-center justify-center p-1 bg-gray-100 dark:bg-gray-850 border dark:border-gray-700">
      <canvas ref={canvasRef} className="hidden" />
      <div
        ref={containerRef}
        className="relative flex items-center justify-center overflow-hidden rounded"
        style={{ pointerEvents: isLoading ? 'none' : undefined }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          ref={imgRef}
          src={imageUrl}
          alt="Selected"
          onLoad={onImgLoad}
          draggable={false}
          crossOrigin="anonymous"
          className="max-h-[calc(50vh-0.5rem)] w-auto rounded pointer-events-none select-none"
        />

        {selection && selection.w > 10 && (
          <>
            <ThreeDStockContentImageLensSelectionOverlay
              selection={selection}
            />
            <ThreeDStockContentImageLensResizeButton
              direction="move"
              selection={selection}
              onPointerDown={onHandlePointerDown}
            />
            <ThreeDStockContentImageLensResizeButton
              direction="nw"
              selection={selection}
              onPointerDown={onHandlePointerDown}
            />
            <ThreeDStockContentImageLensResizeButton
              direction="ne"
              selection={selection}
              onPointerDown={onHandlePointerDown}
            />
            <ThreeDStockContentImageLensResizeButton
              direction="sw"
              selection={selection}
              onPointerDown={onHandlePointerDown}
            />
            <ThreeDStockContentImageLensResizeButton
              direction="se"
              selection={selection}
              onPointerDown={onHandlePointerDown}
            />
            <ThreeDStockContentImageLensResizeButton
              direction="n"
              selection={selection}
              onPointerDown={onHandlePointerDown}
            />
            <ThreeDStockContentImageLensResizeButton
              direction="s"
              selection={selection}
              onPointerDown={onHandlePointerDown}
            />
            <ThreeDStockContentImageLensResizeButton
              direction="e"
              selection={selection}
              onPointerDown={onHandlePointerDown}
            />
            <ThreeDStockContentImageLensResizeButton
              direction="w"
              selection={selection}
              onPointerDown={onHandlePointerDown}
            />
          </>
        )}

        {isLoading && (
          <ThreeDStockContentImageLensLoadingOverlay
            loadingText={loadingText}
          />
        )}
      </div>

      {onClearSearch && (
        <PureIconButtonHelper
          className="absolute top-2 right-2 dark:hover:bg-gray-700 dark:hover:text-gray-200 dark:text-gray-500 hover:bg-gray-200 hover:text-gray-600 p-2 rounded-full text-gray-400"
          icon={IconsEnum.X}
          iconClassName="h-3.5 w-3.5"
          onClick={onClearSearch}
        />
      )}
    </div>
  );
}

export default ThreeDStockContentImageLens;
